<template>
  <div class="fill-height d-flex flex-column">
    <div class="d-flex mb-4">
      <v-spacer />
      <download-button-with-pop-up
        :title="t('download_form.parameter_metadata')"
        :testid="'raster-parameter-metadata'"
        outlined
        :format-choices="[{ title: 'CSV', value: 'csv' }]"
        :file-suffix="' ' + t('download_form.parameter_metadata')"
        @download="downloadParameterMetadata($event)"
      />
      <download-button-with-pop-up
        :title="t('download_form.dataset')"
        :testid="'raster-data'"
        :format-choices="getOutputFormatOptions"
        :file-suffix="' ' + t('download_form.dataset')"
        :show-date-picker="true"
        :date-picker-text-field-props="{
          'error-messages': dateRangeTextFieldErrorMsg,
        }"
        :disable-download-button="!!dateRangeTextFieldErrorMsg"
        :disabled="parameterStore.selectedParameters.length === 0"
        @download="downloadDataset($event)"
      />
    </div>
    <div class="d-flex flex-grow-1">
      <parameter-card
        class="card ml-2"
        :title="parameterCardTitle"
        :items="parameterStore.allParameters"
        :selected="parameterStore.selectedParameters"
        @selected-changed="parameterStore.updateSelectedParameters($event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import DownloadService from "@/service/download-service";
import DatasetStore from "@/stores/dataset/dataset-module";
import ParameterStore from "@/stores/parameter/parameter-module";
import DownloadButtonWithPopUp from "@/components/common/DownloadButtonWithPopUp.vue";
import ParameterCard from "@/components/common/ParameterCard.vue";
import { RasterType } from "@/components/raster/RasterSelectionPage.vue";
import { formatChoice } from "@/util/formatOptions";
import { useI18n } from "vue-i18n";

export default defineComponent ({
  name: 'RasterDownloadPage',
  components: {
    DownloadButtonWithPopUp,
    ParameterCard,
  },
  props: {
    rasterType: { type: Object as PropType<RasterType>, required: true },
  },
  setup(props) {
    const { t } = useI18n();

    const datasetStore = DatasetStore;
    const parameterStore = ParameterStore;    
    const dateRangeTextFieldErrorMsg = '';

    const parameterCardTitle = computed<string>(() => {
      return `${t('parameter.selected_parameters')} (${
        parameterStore.selectedParameters.length
      } ${t('download_form.to')} ${
        parameterStore.allParameters.length
      })`;
    });

    const downloadParameterMetadata = async (obj: {
      fileName: string;
      downloadRange: [Date, Date];
    }): Promise<void> => {
      const url = await DownloadService.getGridParameterMetadataDownloadURL(obj.fileName);
      window.open(url, '_blank');
    };

    const downloadDataset = async (obj: {
      fileName: string;
      downloadRange: [Date, Date];
    }): Promise<void> => {
      let url;
      if (props.rasterType === RasterType.GRID)
        url = await DownloadService.getGridDatasetDownloadURL(obj);
      else if (props.rasterType === RasterType.TIMESERIES)
        url = await DownloadService.getTimeseriesDatasetDownloadURL(obj);
      window.open(url, '_blank');
    };

    const getOutputFormatOptions = computed<formatChoice[]>(() => {
      const options: formatChoice[] = [{ title: 'GeoJSON', value: 'json' }];
      if (props.rasterType === RasterType.TIMESERIES) {
        options.push({ title: 'CSV', value: 'csv' });
      } else if (props.rasterType === RasterType.GRID) {
        options.push({ title: 'NETCDF', value: 'cdf' });
      }
      return options;
    });

    return {
      t,
      datasetStore,
      parameterStore,
      dateRangeTextFieldErrorMsg,
      parameterCardTitle,
      downloadParameterMetadata,
      downloadDataset,
      getOutputFormatOptions,
    }
  }
});
</script>

<style scoped>
/*TODO: duplicate style from ParamterCard.vue*/
.card {
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scrollable-container {
  position: relative;
  height: 100%;
  overflow-y: auto;
}

.width-50 {
  width: 50%;
}
</style>
